import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Injectable, inject, effect } from '@angular/core';
import { Subject, Observable, take, switchMap, EMPTY } from 'rxjs';
import { AuthDataService } from 'src/data-store/auth-data.service';
import { ELoginDeviceType, IRefreshResult } from 'src/models/auth.model';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { AuthLinksService } from './auth-links.service';
import { DeviceService } from './device.service';
import { InAppBrowserAuthService } from './in-app-browser-auth.service';
import { LoggerService } from './logger.service';

/**
 * Coordinates token refresh for 401 Unauthorized responses:
 *  - 401 received (can be multiple, in parallel)
 *  - the first one starts a refresh, the rest park on refreshComplete$
 *  - once tokenData in the store is updated, all parked requests are retried
 *
 * Completion is detected via the AUTH_TOKEN_DATA loading transition, NOT by comparing
 * access_token strings — the backend may legitimately return the same access_token when
 * the current one is still valid, which must still count as a successful refresh.
 *
 * State lives here rather than in the interceptor because a functional interceptor is
 * re-invoked per request and cannot hold in-flight state.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private authDataService = inject(AuthDataService);
  private deviceService = inject(DeviceService);
  private authLinksService = inject(AuthLinksService);
  private inAppBrowserAuthService = inject(InAppBrowserAuthService);
  private loggerService = inject(LoggerService);

  private isRefreshing = false;
  private refreshObserved = false;
  private refreshComplete$ = new Subject<IRefreshResult>();

  public constructor() {
    effect(() => {
      const authError = this.authDataService.authGetError(ELoadingEntity.AUTH_TOKEN_DATA);
      const isLoading = this.authDataService.authIsLoading(ELoadingEntity.AUTH_TOKEN_DATA);

      if (!this.isRefreshing) {
        return;
      }

      if (isLoading) {
        this.refreshObserved = true;
        return;
      }

      if (!this.refreshObserved) {
        return;
      }

      if (authError?.error) {
        this.loggerService.logInfo('Token refresh failed after HTTP 401 error(s), redirecting to login.');
        this.settle({ success: false, error: authError });
      } else {
        this.loggerService.logInfo('Received HTTP 401 error(s), token refreshed, retrying failed requests.');
        this.settle({ success: true, tokenData: this.authDataService.tokenData() ?? undefined });
      }
    });
  }

  public handle401(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const refreshToken = this.authDataService.tokenData()?.refresh_token;

    if (!refreshToken) {
      this.authDataService.resetAuthState();
      return this.redirectToLogin();
    }

    const waitFor$ = this.refreshComplete$; // capture before settle() swaps it

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshObserved = false;
      const refreshType = this.deviceService.isNativePlatform ? ELoginDeviceType.MOBILE : ELoginDeviceType.WEB;
      this.authDataService.refreshToken(refreshType, refreshToken);
    }

    return waitFor$.pipe(
      take(1),
      switchMap(result => {
        if (result.success) {
          return next(this.addTokenHeader(req));
        }
        this.authDataService.resetAuthState();
        return this.redirectToLogin();
      })
    );
  }

  private settle(result: IRefreshResult): void {
    this.isRefreshing = false;
    this.refreshObserved = false;

    const subject = this.refreshComplete$;
    this.refreshComplete$ = new Subject<IRefreshResult>();
    subject.next(result);
    subject.complete();
  }

  private addTokenHeader(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.authDataService.accessToken();
    return token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;
  }

  private redirectToLogin(): Observable<HttpEvent<unknown>> {
    if (this.deviceService.isNativePlatform) {
      this.inAppBrowserAuthService.openInAppBrowser();
    } else {
      window.location.href = this.authLinksService.bcasClientLoginUrl;
    }
    return EMPTY;
  }
}